import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Boxes, ShieldCheck, Zap, MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Stagger, StaggerItem, SplitText } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import Magnetic from "../components/Magnetic";
import SEO from "../components/SEO";
import { STATS, CORE_PRODUCTS, INDUSTRIES } from "../data/content";
import { IndustryIcon } from "../components/icons";

export default function Home() {
  const { tr, lang, isAr } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const Arrow = isAr ? ArrowUpRight : ArrowRight;

  return (
    <div data-testid="home-page">
      <SEO page="home" lang={lang} />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6">
            <Reveal variant="fade">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white/70 px-4 py-1.5 text-xs font-600 tracking-wide text-slate backdrop-blur">
                <span className="h-2 w-2 animate-pulse rounded-full bg-brand-red" />
                {tr.hero.badge}
              </span>
            </Reveal>

            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-900 tracking-tighter leading-[0.98] text-ink">
              <SplitText text={tr.hero.titleA} />
              <span className="block text-gradient">
                <SplitText text={tr.hero.titleB} delay={0.15} />
              </span>
              <Reveal variant="up" delay={0.4}>
                <span className="mt-3 block text-base sm:text-lg font-400 tracking-normal text-slate">{tr.hero.titleC}</span>
              </Reveal>
            </h1>

            <Reveal variant="up" delay={0.5}>
              <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate">{tr.hero.subtitle}</p>
            </Reveal>

            <Reveal variant="up" delay={0.6}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Magnetic strength={0.3}>
                  <Link
                    to="/products"
                    data-testid="hero-cta"
                    className="group flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-600 text-white shadow-xl shadow-brand-blue/25 transition-all hover:bg-brand-blue-dark"
                  >
                    {tr.hero.ctaPrimary}
                    <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Magnetic>
                <Magnetic strength={0.3}>
                  <Link
                    to="/contact"
                    data-testid="hero-cta-secondary"
                    className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 px-7 py-3.5 text-sm font-600 text-ink backdrop-blur transition-all hover:border-brand-red hover:text-brand-red"
                    dir={isAr ? "ltr" : "rtl"}
                  >
                    {tr.hero.ctaSecondary}
                  </Link>
                </Magnetic>
              </div>
            </Reveal>

            <Stagger className="mt-12 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4" stagger={0.1}>
              {STATS.map((s) => (
                <StaggerItem key={s.value}>
                  <p className="text-3xl sm:text-4xl font-800 tracking-tight text-ink">{s.value}</p>
                  <p className="mt-1 text-xs sm:text-sm text-slate">{isAr ? s.ar : s.en}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          {/* Hero image */}
          <div className="lg:col-span-6">
            <Reveal variant="scale">
              <div className="relative">
                <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-brand-blue/15 via-transparent to-brand-red/10 blur-2xl" />
                <div className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-2xl shadow-brand-blue/10">
                  <motion.img
                    src="/assets/products/hero-collage.png"
                    alt="ICON Trading Co. industrial supplies"
                    style={{ y: imgY, scale: imgScale }}
                    className="h-[340px] w-full object-cover sm:h-[480px]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/30 via-transparent to-transparent" />
                </div>
                {/* floating chips */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                  className="absolute -bottom-5 start-6 flex items-center gap-3 rounded-2xl border border-border bg-white/90 px-4 py-3 shadow-xl backdrop-blur"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-blue/10"><Boxes className="h-5 w-5 text-brand-blue" /></span>
                  <div>
                    <p className="text-sm font-700 text-ink">8 {tr.products.inStock}</p>
                    <p className="text-[11px] text-slate">{tr.homeCaps.overline}</p>
                  </div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal variant="fade" delay={0.8} className="mt-16 hidden sm:flex justify-center">
          <span className="flex items-center gap-2 text-xs tracking-ultra uppercase text-slate">
            <span className="h-8 w-px animate-pulse bg-gradient-to-b from-brand-blue to-transparent" />
            {tr.hero.scroll}
          </span>
        </Reveal>
      </section>

      {/* MARQUEE */}
      <div className="relative border-y border-border bg-surface/60 py-5 overflow-hidden">
        <div className="marquee-track animate-marquee gap-8">
          {[0, 1].map((k) => (
            <span key={k} className="flex shrink-0 items-center gap-8 pe-8 text-lg sm:text-2xl font-700 tracking-tight text-ink/70">
              {tr.marquee.split("·").filter(Boolean).map((w, i) => (
                <span key={i} className="flex items-center gap-8">
                  {w.trim()}
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-red" />
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* CAPABILITIES PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading overline={tr.homeCaps.overline} title={tr.homeCaps.title} desc={tr.homeCaps.desc} />
        <Stagger className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4" stagger={0.07}>
          {CORE_PRODUCTS.map((p) => (
            <StaggerItem key={p.id} variant="up">
              <Link to="/products" className="group block overflow-hidden rounded-2xl border border-border bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/10">
                <div className="relative aspect-square overflow-hidden bg-surface">
                  <img src={p.image} alt={p[lang].name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                </div>
                <div className="flex items-center justify-between gap-2 p-4">
                  <span className="text-sm font-600 text-ink leading-tight">{p[lang].name}</span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-slate transition-all group-hover:text-brand-blue group-hover:rotate-0" />
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal variant="up" className="mt-12 flex justify-center">
          <Link to="/products" className="group inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-600 text-ink transition-all hover:bg-ink hover:text-white">
            {tr.homeCaps.cta}
            <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* SHOWROOM TEASER */}
      <section className="relative overflow-hidden bg-surface py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <Reveal variant="left" className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl">
              <img src="/assets/showroom.jpeg" alt="ICON Trading Co. showroom Dammam" className="h-[300px] w-full object-cover sm:h-[460px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 start-5 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-600 text-ink backdrop-blur">
                <MapPin className="h-3.5 w-3.5 text-brand-red" /> Dammam · K.S.A.
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-5">
            <SectionHeading overline={tr.homeShowroom.overline} title={tr.homeShowroom.title} desc={tr.homeShowroom.desc} />
            <Reveal variant="up" delay={0.15}>
              <Link to="/about" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-600 text-white transition-all hover:bg-brand-blue-dark">
                {tr.homeShowroom.cta}
                <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading overline={tr.homeIndustries.overline} title={tr.homeIndustries.title} desc={tr.homeIndustries.desc} center />
        <Stagger className="mt-14 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" stagger={0.06}>
          {INDUSTRIES.map((ind) => (
            <StaggerItem key={ind.id} variant="scale">
              <div className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-white p-5 text-center transition-all hover:-translate-y-1 hover:border-brand-red hover:shadow-lg">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-blue/8 text-brand-blue transition-colors group-hover:bg-brand-red/10 group-hover:text-brand-red">
                  <IndustryIcon name={ind.icon} className="h-6 w-6" />
                </span>
                <span className="text-xs font-600 leading-tight text-ink">{isAr ? ind.ar : ind.en}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* CTA BAND */}
      <section className="px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal variant="scale" className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-blue px-8 py-16 sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-red/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="grain absolute inset-0 opacity-[0.06]" />
            <div className="relative mx-auto max-w-3xl">
              <h2 className="text-2xl sm:text-4xl font-800 tracking-tight leading-tight text-white">{tr.homeCta.title}</h2>
              <p className="mt-5 text-base sm:text-lg text-white/70">{tr.homeCta.desc}</p>
              <Magnetic strength={0.3} className="mt-9 inline-block">
                <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-700 text-brand-blue transition-all hover:bg-brand-red hover:text-white">
                  {tr.homeCta.cta}
                  <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
